import {
  editableFieldOf,
  type GapDraftEditChange,
  type GapDraftView,
} from '@momentum/types';

import { useGapDraftEdits } from '@/shared/hooks/useGapReview';
import { formatEditValue } from '../../lib/draft-edit';
import { dateTime, pluralize } from '../../lib/format';
import { DiffKind, diffWords } from '../../lib/word-diff';

const DIFF_CLASS: Record<DiffKind, string> = {
  [DiffKind.SAME]: 'gr-diff-same',
  [DiffKind.REMOVED]: 'gr-diff-removed',
  [DiffKind.ADDED]: 'gr-diff-added',
};

/**
 * One change, rendered as what actually moved.
 *
 * Three shapes, because the three kinds of change read differently:
 *
 * - Authored — nothing to compare against, so there is no diff to draw. The
 *   note carries the meaning and the value stands alone.
 * - A score — two short numbers. Word-diffing `0.75` against `0.40` highlights
 *   the whole token either way, so an arrow says it with less ink.
 * - Prose — the case the diff exists for: a few words inside a paragraph the
 *   agent otherwise got right.
 */
function ChangeBody({ change }: { change: GapDraftEditChange }) {
  if (change.before === undefined) {
    return (
      <>
        <p className="gr-edit-authored">
          Authored — no agent had written this field.
        </p>
        <p className="gr-edit-after">{formatEditValue(change.after)}</p>
      </>
    );
  }

  if (typeof change.before !== 'string' || typeof change.after !== 'string') {
    return (
      <p className="gr-edit-scorediff">
        <span className="gr-diff-removed">
          {formatEditValue(change.before)}
        </span>
        <span className="gr-edit-arrow">→</span>
        <span className="gr-diff-added">{formatEditValue(change.after)}</span>
      </p>
    );
  }

  return (
    <p className="gr-edit-diff">
      {diffWords(change.before, change.after).map((segment, index) => (
        <span
          // Segments have no identity of their own and the list is rebuilt
          // whole whenever the change does, so position is the only key there
          // is — and a stable one.
          key={index}
          className={DIFF_CLASS[segment.kind]}
        >
          {segment.text}
        </span>
      ))}
    </p>
  );
}

/**
 * The human edit history — what a reviewer changed, from what, and why.
 *
 * Fetched when the tab opens rather than with the queue: it grows without bound
 * and nobody reads it while walking the drafts.
 *
 * A change with no `before` is not a gap in the record. It says the field held
 * nothing when the human wrote it — that they AUTHORED it rather than corrected
 * an agent — and that difference is the point of keeping the history at all.
 * `roiImpact` has no agent behind it, so it can only ever appear that way.
 * `severity` is the opposite case: the enrichment authors it and says why in
 * `severityReasoning`, so an edit here is a reviewer overruling an argument
 * that is still on the page beside the number they replaced.
 */
export function EditsTab({ draft }: { draft: GapDraftView }) {
  const editsQuery = useGapDraftEdits(draft._id, true);
  const edits = editsQuery.data?.edits ?? [];

  return (
    <div>
      <h3 className="gr-insp-h">
        Edits · {pluralize(draft.editedPaths.length, 'field')} changed
      </h3>

      {editsQuery.isPending && (
        <div className="gr-empty">
          <b>Loading</b>Reading this draft's edit history.
        </div>
      )}

      {editsQuery.isError && (
        <div className="gr-empty">
          <b>Could not load the history</b>
          {editsQuery.error.message}
        </div>
      )}

      {editsQuery.isSuccess && edits.length === 0 && (
        <div className="gr-empty">
          <b>No edits</b>Every value on this draft is as the agents left it.
        </div>
      )}

      <div className="gr-edits">
        {edits.map((edit) => (
          <div className="gr-edit-entry" key={edit._id}>
            <div className="gr-comment-top">
              <span className="gr-comment-who">
                {edit.authorName ?? edit.employeeId}
              </span>
              <span className="gr-comment-when">
                {dateTime(edit.createdAt)}
              </span>
            </div>

            {edit.changes.map((change) => (
              <div className="gr-edit-change" key={change.path}>
                <div className="gr-edit-change-head">
                  <span className="gr-edit-label">
                    {editableFieldOf(change.path)?.label ?? change.path}
                  </span>
                  <code className="gr-edit-path">{change.path}</code>
                </div>

                <ChangeBody change={change} />

                {change.reason !== undefined && (
                  <p className="gr-edit-reason-shown">Why: {change.reason}</p>
                )}
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}
